// ═══════════════════════════════════════════════════════════════════════
// CompanyPnlPage — Sprint 2026-Q1d / Step ⑦ · Pass 2
// ═══════════════════════════════════════════════════════════════════════
// The OFFICIAL Schedule III statutory P&L of the company.
//
// Lines that depend on the Journal Voucher module (not yet built) show
// ₹0 with the approved disclosure note. The page mechanically
// reflects the frozen mapping — no estimates, no plug numbers.
//
// Export targets: CSV · PDF · Print layout.
// Reconciliation: 7 checks tying Company line totals back to the
// dimensional reports (Project / Branch / Vertical).
// ═══════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { ViewOverlay } from "@/hooks/useViewOverlay";
import { useFormTab } from "@/hooks/useFormTab";
import {
  Filter as FilterIcon, RefreshCw, Download, FileText, X,
  CheckCircle2, AlertTriangle, ArrowRight, Printer, BookLock,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const currentFy = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  const startYear = m >= 3 ? y : y - 1;
  return {
    from_period: `${startYear}-04`,
    to_period:   `${(startYear + 1).toString().padStart(4, "0")}-03`,
  };
};

const DRILLABLE = new Set(["REVENUE_OPS", "COST_MAT_CONSUMED",
                            "PURCHASES_STK", "DEPRECIATION"]);


export default function CompanyPnlPage() {
  const openFormTab = useFormTab();
  const [{ from_period, to_period }, setRange] = useState(currentFy());
  const [comparative, setComparative] = useState(false);
  const [cmpRange, setCmpRange] = useState({ cmp_from_period: "", cmp_to_period: "" });
  const [data, setData] = useState(null);
  const [reconcile, setReconcile] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from_period, to_period, comparative };
      if (comparative && cmpRange.cmp_from_period) params.cmp_from_period = cmpRange.cmp_from_period;
      if (comparative && cmpRange.cmp_to_period)   params.cmp_to_period   = cmpRange.cmp_to_period;
      const [res, rec] = await Promise.all([
        api.get("/reports/company-pnl/_summary", { params }),
        api.get("/reports/company-pnl/_reconcile", {
          params: { from_period, to_period },
        }),
      ]);
      setData(res.data || null);
      setReconcile(rec.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Company P&L.");
    } finally {
      setLoading(false);
    }
  }, [from_period, to_period, comparative, cmpRange]);

  useEffect(() => { load(); }, [load]);

  const openDrill = (line) => {
    if (!DRILLABLE.has(line.code) || !line.drillable) return;
    openFormTab({
      path: `/reports/company-pnl/drill/${line.code}`,
      label: `${line.name} · ${from_period} → ${to_period}`,
      parent: "/reports/company-pnl",
      render: ({ close }) => (
        <CompanyPnlDrillTab
          line={line}
          from_period={from_period}
          to_period={to_period}
          onClose={close}
        />
      ),
    });
  };

  const exportFile = async (kind) => {
    try {
      const params = new URLSearchParams({
        from_period, to_period, comparative: String(comparative),
      });
      if (comparative && cmpRange.cmp_from_period) params.append("cmp_from_period", cmpRange.cmp_from_period);
      if (comparative && cmpRange.cmp_to_period)   params.append("cmp_to_period",   cmpRange.cmp_to_period);
      const url = `/reports/company-pnl/export.${kind}?${params}`;
      const resp = await api.get(url, { responseType: "blob" });
      const blob = new Blob([resp.data],
                            { type: kind === "csv" ? "text/csv" : "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `company-pnl-${from_period}_${to_period}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${kind.toUpperCase()} downloaded.`);
    } catch {
      toast.error(`Failed to export ${kind.toUpperCase()}.`);
    }
  };

  const printPage = () => window.print();
  const reconcilePass = reconcile?.checks?.every(c => c.status === "PASS");
  const lines = data?.lines || [];
  const cmpAmts = data?.comparative?.amounts || {};
  const cmpRangeLabel = data?.comparative
    ? `${data.comparative.period_from || "ALL"} → ${data.comparative.period_to || "ALL"}`
    : null;

  return (
    <div className="space-y-6 p-6 print:p-0" data-testid="company-pnl-page">
      <Toaster richColors position="top-right" />

      {/* No-print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { background: white; }
        }
        .print-only { display: none; }
      `}</style>

      <div className="no-print">
        <PageHeader
          eyebrow="Step ⑦ · Pass 2 · Schedule III"
          title="Statement of Profit and Loss (Company P&L)"
          subtitle="Official statutory P&L mapped to Schedule III of the Companies Act, 2013. Lines pending the Journal Voucher module disclose ₹0 with their approved note. No estimates, no plug numbers."
          actions={
            <div className="flex items-center gap-2">
              <button
                data-testid="company-pnl-refresh-btn"
                onClick={load}
                disabled={loading}
                className="text-xs uppercase tracking-wider font-bold px-3 py-2 border border-slate-300 hover:bg-slate-50">
                <RefreshCw size={12} className={`inline mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
              </button>
              <button
                data-testid="company-pnl-print-btn"
                onClick={printPage}
                className="text-xs uppercase tracking-wider font-bold px-3 py-2 border border-slate-300 hover:bg-slate-50">
                <Printer size={12} className="inline mr-1" /> Print
              </button>
              <button
                data-testid="company-pnl-export-csv-btn"
                onClick={() => exportFile("csv")}
                className="text-xs uppercase tracking-wider font-bold px-3 py-2 border border-slate-300 hover:bg-slate-50">
                <Download size={12} className="inline mr-1" /> CSV
              </button>
              <button
                data-testid="company-pnl-export-pdf-btn"
                onClick={() => exportFile("pdf")}
                className="text-xs uppercase tracking-wider font-bold px-3 py-2 bg-slate-900 text-white hover:bg-slate-800">
                <FileText size={12} className="inline mr-1" /> PDF
              </button>
            </div>
          }
        />

        {/* Filter bar */}
        <Card className="p-4 mt-6">
          <div className="flex items-center gap-2 mb-3">
            <FilterIcon size={14} className="text-slate-500" />
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Filters</div>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
            <FilterField label="Period From">
              <input
                data-testid="company-pnl-filter-from"
                type="month"
                value={from_period}
                onChange={e => setRange(r => ({ ...r, from_period: e.target.value }))}
                className={inputCls}
              />
            </FilterField>
            <FilterField label="Period To">
              <input
                data-testid="company-pnl-filter-to"
                type="month"
                value={to_period}
                onChange={e => setRange(r => ({ ...r, to_period: e.target.value }))}
                className={inputCls}
              />
            </FilterField>
            <FilterField label="Comparative">
              <label className="flex items-center gap-2 text-xs h-[34px]">
                <input
                  data-testid="company-pnl-filter-comparative"
                  type="checkbox"
                  checked={comparative}
                  onChange={e => setComparative(e.target.checked)}
                  className="h-4 w-4"
                />
                Show comparative period
              </label>
            </FilterField>
            {comparative && (
              <>
                <FilterField label="Comparative From">
                  <input
                    data-testid="company-pnl-filter-cmp-from"
                    type="month"
                    value={cmpRange.cmp_from_period}
                    onChange={e => setCmpRange(c => ({ ...c, cmp_from_period: e.target.value }))}
                    placeholder="(default: prior FY)"
                    className={inputCls}
                  />
                </FilterField>
                <FilterField label="Comparative To">
                  <input
                    data-testid="company-pnl-filter-cmp-to"
                    type="month"
                    value={cmpRange.cmp_to_period}
                    onChange={e => setCmpRange(c => ({ ...c, cmp_to_period: e.target.value }))}
                    className={inputCls}
                  />
                </FilterField>
              </>
            )}
          </div>
        </Card>

        {/* Reconciliation banner */}
        {reconcile && (
          <Card className={`p-4 border mt-6 ${reconcilePass ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`}>
            <div className="flex items-start gap-3" data-testid="company-pnl-reconcile-banner">
              {reconcilePass
                ? <CheckCircle2 size={18} className="text-emerald-700 flex-none mt-0.5" />
                : <AlertTriangle size={18} className="text-amber-700 flex-none mt-0.5" />}
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-700">
                  Reconciliation — Schedule III vs Dimensional Reports
                </div>
                <div className="font-display text-base font-black text-slate-900 mt-0.5">
                  {reconcilePass
                    ? "All 7 checks PASS — Company P&L ties to Project, Branch and Vertical totals."
                    : `${reconcile.checks.filter(c => c.status === "FAIL").length} of ${reconcile.checks.length} check(s) failing.`}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-2">
                  {reconcile.checks.map((c, i) => (
                    <div key={i}
                         data-testid={`company-pnl-reconcile-${i}`}
                         className={`text-xs px-2 py-1 rounded ${c.status === "PASS"
                                      ? "bg-emerald-100 text-emerald-900"
                                      : "bg-amber-100 text-amber-900"}`}>
                      <div className="font-bold">{c.status} · {c.check}</div>
                      {c.status === "FAIL" && (
                        <div className="text-[10px] mt-0.5">
                          Δ ₹{c.delta.toLocaleString("en-IN")} (Company: ₹{c.company.toLocaleString("en-IN")} · Source: ₹{c.source.toLocaleString("en-IN")})
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {!reconcilePass && (
                  <div className="text-[11px] text-slate-600 mt-2 italic">
                    Drift here is usually orphan voucher / snapshot rows that don&apos;t link to a live foreign-key target. Step ⑧ Financial Integrity Dashboard surfaces drill-down to fix.
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Print header (visible only in print) */}
      <div className="print-only border-b border-slate-300 pb-4 mb-4">
        <div className="text-center">
          <div className="text-xs uppercase tracking-widest text-slate-500">Radiants ERP</div>
          <div className="text-2xl font-black mt-1">Statement of Profit and Loss</div>
          <div className="text-xs text-slate-600 mt-1">
            For the period {from_period || "ALL"} → {to_period || "ALL"}
            {cmpRangeLabel && <span> · Comparative: {cmpRangeLabel}</span>}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">
            Pursuant to Schedule III of the Companies Act, 2013
          </div>
        </div>
      </div>

      {/* Schedule III table */}
      {loading ? (
        <Card className="p-12 text-center text-slate-500">Loading Company P&L…</Card>
      ) : !data ? (
        <EmptyState title="No data" hint="Try a different period." />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm" data-testid="company-pnl-table">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="text-left text-[10px] font-bold uppercase tracking-wider py-3 px-3 w-1/2">
                  Particulars
                </th>
                <th className="text-right text-[10px] font-bold uppercase tracking-wider py-3 px-3">
                  Amount (₹)
                  <div className="text-[9px] font-normal opacity-70 mt-0.5">
                    {from_period} → {to_period}
                  </div>
                </th>
                {comparative && (
                  <th className="text-right text-[10px] font-bold uppercase tracking-wider py-3 px-3">
                    Comparative (₹)
                    <div className="text-[9px] font-normal opacity-70 mt-0.5">
                      {cmpRangeLabel}
                    </div>
                  </th>
                )}
                <th className="text-left text-[10px] font-bold uppercase tracking-wider py-3 px-3 no-print">
                  Note
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map(line => <Row key={line.code} line={line} cmp={cmpAmts[line.code]}
                                       comparative={comparative} openDrill={openDrill} />)}
            </tbody>
          </table>
        </Card>
      )}

      {/* Disclosure footer */}
      {data && (
        <Card className="p-4 bg-slate-50 border-l-4 border-amber-500" data-testid="company-pnl-disclosure">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-800 font-bold mb-2">
            <BookLock size={11} className="inline mr-1" /> Disclosure
          </div>
          <div className="text-xs text-slate-700 space-y-1">
            <div>
              Lines marked &ldquo;Journal Voucher module pending&rdquo; currently disclose <b>₹0</b> pending the JV module — these include Other Income, Employee Benefits, Finance Cost, Other Expenses, Current Tax and Deferred Tax.
            </div>
            <div>
              The figures shown here are computed exclusively from source documents (Sales Invoices, Issue Vouchers, Purchase Invoices, Inventory Batches, Depreciation Snapshots and Adjustments) with no estimates or plug numbers.
            </div>
            <div className="text-[10px] text-slate-500 mt-2">
              Computed at: {data.computed_at}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}


// ─── Schedule III row ───────────────────────────────────────────────────────

function Row({ line, cmp, comparative, openDrill }) {
  const isHeading = line.code.startsWith("TOTAL_");
  const isResult  = line.code === "PBT" || line.code === "PAT";
  const isTaxTotal = line.code === "TAX_TOTAL";
  const isJv = line.jv_dependent;
  const fmt = (v) => (v === null || v === undefined)
    ? "—"
    : v === 0
    ? <span className="text-slate-400">—</span>
    : formatINR(v);

  const bg =
    isResult     ? "bg-indigo-50 font-black text-indigo-900" :
    isHeading    ? "bg-slate-100 font-bold text-slate-900"   :
    isTaxTotal   ? "bg-slate-50 font-bold text-slate-900"    :
                   "border-b border-slate-100 hover:bg-slate-50";

  const indented = !isHeading && !isResult && !isTaxTotal;

  return (
    <tr data-testid={`company-pnl-row-${line.code}`} className={bg}>
      <td className={`py-2.5 px-3 ${indented ? "pl-8" : ""}`}>
        <span className={isJv ? "text-amber-700 italic" : ""}>
          {line.name}
        </span>
        {isJv && (
          <span className="ml-2 text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold">
            JV pending
          </span>
        )}
      </td>
      <td
        className={`py-2.5 px-3 text-right tabular-nums ${line.drillable ? "cursor-pointer hover:text-[#F97316] hover:underline" : ""}`}
        data-testid={line.drillable ? `company-pnl-drill-${line.code}` : undefined}
        onClick={line.drillable ? () => openDrill(line) : undefined}
        title={line.drillable ? "Click to drill down" : ""}>
        {fmt(line.amount)}
        {line.drillable && <ArrowRight size={10} className="inline ml-1 opacity-40" />}
      </td>
      {comparative && (
        <td className="py-2.5 px-3 text-right tabular-nums">
          {fmt(cmp)}
        </td>
      )}
      <td className="py-2.5 px-3 text-[10px] text-slate-500 max-w-md no-print">
        {line.note}
      </td>
    </tr>
  );
}


// ─── Drill-down tab ─────────────────────────────────────────────────────────

function CompanyPnlDrillTab({ line, from_period, to_period, onClose }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/company-pnl/drill/${line.code}`, {
      params: { from_period, to_period, limit: 500 },
    })
      .then(r => setData(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load drill-down."))
      .finally(() => setLoading(false));
  }, [line.code, from_period, to_period]);

  return (
    <ViewOverlay onClose={onClose} testid="company-pnl-drill-overlay">
      <Toaster richColors position="top-right" />
      <div className="p-6 space-y-4">
        <PageHeader
          eyebrow={`Drill-Down · ${line.code}`}
          title={line.name}
          subtitle={`${from_period} → ${to_period}`}
          actions={
            <button onClick={onClose}
                    className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900">
              <X size={16} className="inline mr-1" /> Close
            </button>
          }
        />
        {loading
          ? <div className="text-center text-slate-500 py-12">Loading source documents…</div>
          : data.length === 0
          ? <EmptyState title="No source documents in this scope" hint="Try widening the period." />
          : <Card className="overflow-x-auto">
              <DrillTable code={line.code} rows={data} />
            </Card>}
      </div>
    </ViewOverlay>
  );
}


function DrillTable({ code, rows }) {
  if (code === "REVENUE_OPS") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-revenue">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Invoice #</th><th className="text-left p-3">Date</th><th className="text-left p-3">Client</th><th className="text-left p-3">Status</th><th className="text-right p-3">Total</th></tr>
        </thead>
        <tbody>{rows.map(r => (
          <tr key={r.id} className="border-b border-slate-100">
            <td className="p-3 font-mono font-bold text-[#F97316]">{r.invoice_number}</td>
            <td className="p-3 text-xs">{r.invoice_date}</td>
            <td className="p-3 text-xs">{r.client_name || "—"}</td>
            <td className="p-3 text-xs">{r.status}</td>
            <td className="p-3 text-right tabular-nums font-bold text-emerald-700">{formatINR(r.total_amount || 0)}</td>
          </tr>
        ))}</tbody>
      </table>
    );
  }
  if (code === "COST_MAT_CONSUMED") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-iv">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Voucher #</th><th className="text-left p-3">Date</th><th className="text-left p-3">Source</th><th className="text-left p-3">Project</th><th className="text-right p-3">Total</th></tr>
        </thead>
        <tbody>{rows.map(r => (
          <tr key={r.id} className="border-b border-slate-100">
            <td className="p-3 font-mono font-bold text-[#F97316]">{r.voucher_number}</td>
            <td className="p-3 text-xs">{r.voucher_date}</td>
            <td className="p-3 text-xs">{r._source || "Issue Voucher"}</td>
            <td className="p-3 text-xs">{r.project_name || "—"}</td>
            <td className="p-3 text-right tabular-nums font-bold text-amber-800">-{formatINR(r.total_amount || 0)}</td>
          </tr>
        ))}</tbody>
      </table>
    );
  }
  if (code === "PURCHASES_STK") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-pi">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">PI #</th><th className="text-left p-3">Date</th><th className="text-left p-3">Vendor</th><th className="text-right p-3">Total</th></tr>
        </thead>
        <tbody>{rows.map(r => (
          <tr key={r.id} className="border-b border-slate-100">
            <td className="p-3 font-mono font-bold text-[#F97316]">{r.invoice_number}</td>
            <td className="p-3 text-xs">{r.invoice_date}</td>
            <td className="p-3 text-xs">{r.vendor_name || "—"}</td>
            <td className="p-3 text-right tabular-nums font-bold text-amber-800">-{formatINR(r.total_amount || 0)}</td>
          </tr>
        ))}</tbody>
      </table>
    );
  }
  if (code === "DEPRECIATION") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-dep">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Period</th><th className="text-left p-3">Asset</th><th className="text-right p-3">Opening NBV</th><th className="text-right p-3">Depreciation</th><th className="text-right p-3">Closing NBV</th></tr>
        </thead>
        <tbody>{rows.map(r => (
          <tr key={r.id} className="border-b border-slate-100">
            <td className="p-3 font-mono font-bold">{r.period_key}</td>
            <td className="p-3 text-xs">
              <span className="font-mono font-bold text-[#F97316]">{r.asset_code}</span>
              <span className="text-slate-400 ml-1">· {r.asset_name}</span>
            </td>
            <td className="p-3 text-right tabular-nums">{formatINR(r.opening_nbv || 0)}</td>
            <td className="p-3 text-right tabular-nums font-bold text-amber-800">-{formatINR(r.monthly_depreciation || 0)}</td>
            <td className="p-3 text-right tabular-nums">{formatINR(r.closing_nbv || 0)}</td>
          </tr>
        ))}</tbody>
      </table>
    );
  }
  return null;
}


// ─── Small helpers ──────────────────────────────────────────────────────────

const inputCls = "w-full border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#F97316]";

function FilterField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
